import { Col, Divider, Form, Input, Row, Select, Typography } from 'antd';
import { Create, useForm, useSelect } from '@refinedev/antd';
import { useEffect } from 'react';
import { IProfessor } from 'interfaces/professor';
import { InputMask } from 'components/form-items/InputMask';

export const ProfessorCreate = () => {
  const { formProps, saveButtonProps } = useForm<IProfessor>();
  const stateId = Form.useWatch('state_id', formProps.form);
  const { selectProps: stateSelectProps } = useSelect({
    resource: 'states',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 30,
    pagination: { mode: 'server' }
  });
  const { selectProps: citiesSelectProps, queryResult: citiesQueryResult } =
    useSelect({
      resource: 'cities',
      optionLabel: 'name',
      optionValue: 'id',
      fetchSize: 999,
      filters: [
        {
          field: 'filter[by_state_id]',
          operator: 'eq',
          value: stateId
        }
      ],
      pagination: { mode: 'server' }
    });

  useEffect(() => {
    // Sempre limpar a cidade quando o estado mudar
    formProps.form?.setFieldsValue({ city_id: undefined });
  }, [stateId, formProps.form]);

  return (
    <Create saveButtonProps={saveButtonProps} title="Criar professor">
      <Form {...formProps} layout="vertical">
        <Typography.Title level={5} style={{ margin: '24px 0' }}>
          Dados Pessoais
        </Typography.Title>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="E-mail" name="email">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="Telefone"
              name="phone_number"
              rules={[
                {
                  pattern: /^\(\d{2}\) \d{5}-\d{4}$/,
                  message: 'Número inválido'
                }
              ]}
            >
              <InputMask mask="(00) 00000-0000" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Data de nascimento" name="birthdate">
              <Input type="date" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="Gênero" name="gender">
              <Select placeholder="Selecione um gênero..." allowClear>
                <Select.Option value="male">Masculino</Select.Option>
                <Select.Option value="female">Feminino</Select.Option>
                <Select.Option value="non_binary">Não binário</Select.Option>
                <Select.Option value="prefer_not_to_say">
                  Prefiro não informar
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Nacionalidade" name="nationality">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="Estado" name="state_id">
              <Select
                {...stateSelectProps}
                placeholder="Selecione um estado..."
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Cidade" name="city_id">
              <Select
                {...citiesSelectProps}
                options={stateId ? citiesSelectProps.options : []}
                loading={citiesQueryResult.fetchStatus === 'fetching'}
                disabled={!stateId}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Selecione uma cidade..."
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="CPF/Documento"
              name="document_number"
              rules={[
                {
                  pattern: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
                  message: 'CPF inválido'
                }
              ]}  
            >
              <InputMask mask="000.000.000-00" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="RG/Identidade" name="identity_number">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Typography.Title level={5} style={{ margin: '12px 0' }}>
          Titulação acadêmica e Perfil profissional
        </Typography.Title>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="Título" name="title">
              <Select
                placeholder="Selecione um titulo..."
                style={{ minWidth: 400 }}
              >
                <Select.Option value="specialist">Especialista</Select.Option>
                <Select.Option value="master">Mestre</Select.Option>
                <Select.Option value="doctor">Doutor</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Vínculo empregatício" name="employment_type">
              <Select placeholder="Selecione um vínculo..." allowClear>
                <Select.Option value="pj">PJ</Select.Option>
                <Select.Option value="clt">CLT</Select.Option>
                <Select.Option value="rpa">RPA</Select.Option>
                <Select.Option value="hourly">Horista</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="Lattes ID" name="lattes_id">
              <Input placeholder="ID do Lattes" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Lattes (URL)" name="lattes_url">
              <Input type="url" placeholder="https://lattes.cnpq.br/..." />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="LinkedIn (URL)" name="linkedin_url">
              <Input type="url" placeholder="https://www.linkedin.com/in/..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Biografia" name="bio">
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};

